import { ArrowDown, ArrowUpRight } from "lucide-react";
import HeroSeeds from "@/components/HeroSeeds";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_therapy-warm/artifacts/x4mtonsw_ChatGPT%20Image%2014.%20Juni%202026%2C%2017_04_00.png";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden"
    >
      {/* Background dandelion image with warm overlay + slow Ken Burns motion */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 overflow-hidden"
      >
        <div
          className="absolute inset-0 hero-bg-anim"
          style={{
            backgroundImage: `url("${HERO_IMAGE}")`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            transform: "scaleX(-1)",
          }}
        />
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(105deg, rgba(251,247,240,0.96) 0%, rgba(251,247,240,0.82) 32%, rgba(214,227,238,0.20) 62%, rgba(214,227,238,0.05) 88%, rgba(214,227,238,0.18) 100%)",
        }}
      />

      {/* Seeds drifting away from the dandelion */}
      <HeroSeeds count={22} />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <p
            className="font-serif italic text-lg sm:text-xl reveal"
            data-testid="hero-name"
            style={{ color: "var(--sky-deep)" }}
          >
            Petra Fellmann
          </p>
          <p
            className="font-serif text-xl sm:text-2xl mt-3 reveal reveal-delay-1"
            data-testid="hero-title"
            style={{ color: "var(--ink)", fontWeight: 500, letterSpacing: "-0.005em" }}
          >
            Heilpraktikerin für Psychotherapie
          </p>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-6xl lg:text-7xl mt-8 reveal reveal-delay-2"
            style={{ color: "var(--ink)" }}
          >
            Der Raum für
            <br />
            <em className="font-serif italic" style={{ color: "var(--sky-deep)" }}>deine Seele</em>
          </h1>

          <p
            data-testid="hero-subline"
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed reveal reveal-delay-3"
            style={{ color: "var(--ink-soft)" }}
          >
            Ein Ort, an dem du mit Wertschätzung und Achtsamkeit in deinem Tempo
            wachsen darfst, dich angenommen fühlst und neue Kraft für deinen eigenen Weg
            schöpfen kannst.
          </p>

          <p
            data-testid="hero-specialty"
            className="mt-6 max-w-xl text-[15px] reveal reveal-delay-3"
            style={{ color: "var(--ink)" }}
          >
            <span className="font-serif italic" style={{ color: "var(--sky-deep)" }}>
              Traumatherapie &amp; Systemische Therapie
            </span>
            <span style={{ color: "var(--ink-soft)" }}> · Beratung · Coaching · Therapie</span>
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal reveal-delay-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#kontakt")}
              className="btn-primary"
            >
              Termin anfragen
              <ArrowUpRight size={18} />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#motivation")}
              className="btn-secondary"
            >
              Mehr erfahren
              <ArrowDown size={16} />
            </button>
          </div>

          {/* Trust strip */}
          <div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4 reveal reveal-delay-5">
            <div className="flex flex-col">
              <span className="font-serif text-3xl" style={{ color: "var(--ink)" }}>30+</span>
              <span className="text-xs tracking-widest uppercase" style={{ color: "var(--ink-mute)" }}>
                Jahre mit Menschen
              </span>
            </div>
            <div className="w-px h-10" style={{ background: "var(--line)" }} />
            <div className="flex flex-col">
              <span className="font-serif text-3xl" style={{ color: "var(--ink)" }}>1:1</span>
              <span className="text-xs tracking-widest uppercase" style={{ color: "var(--ink-mute)" }}>
                Individuelle Begleitung
              </span>
            </div>
            <div className="w-px h-10 hidden sm:block" style={{ background: "var(--line)" }} />
            <div className="flex flex-col">
              <span className="font-serif text-3xl" style={{ color: "var(--ink)" }}>VFP</span>
              <span className="text-xs tracking-widest uppercase" style={{ color: "var(--ink-mute)" }}>
                Verbandsmitglied
              </span>
            </div>
          </div>
        </div>

        {/* Right side intentionally empty — background dandelion shines through */}
        <div className="lg:col-span-5 hidden lg:block" aria-hidden="true" />
      </div>

      {/* Hidden img kept for SEO */}
      <img
        src={HERO_IMAGE}
        alt="Pusteblume mit fliegenden Samen — Symbol für Hoffnung und Leichtigkeit"
        className="sr-only"
        data-testid="hero-image"
      />

      {/* Soft section transition */}
      <div className="mt-24 flex justify-center reveal reveal-delay-5">
        <div className="divider-line" />
      </div>
    </section>
  );
}
